/**
 * Tells the API that somebody is at the screen (PT-171).
 *
 * The API ends a session 30 minutes after its last request, and reading a dossier, scrolling a
 * document or typing a correction sends none. So the reader's gestures — a key, the pointer, a
 * scroll, a touch — are reported, at most once a minute. The API stays the one that ends the
 * session: a page left alone reports nothing, and a closed browser cannot.
 */
import { useEffect } from "react";
import API from "@app/services/api";

const GESTURES = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
] as const;
// The resolution of « inactif », not a threshold: a session lapses 29 to 30 minutes after the last gesture.
const REPORT_EVERY_MS = 60_000;

let lastReport = 0;

function onGesture(): void {
  const now = Date.now();
  if (now - lastReport < REPORT_EVERY_MS) return;
  lastReport = now;
  void API.post({ path: "/user/session/presence" });
}

function listen(target: Document): () => void {
  // Captured, because a scroll does not bubble and a component may stop the others.
  for (const gesture of GESTURES) {
    target.addEventListener(gesture, onGesture, {
      capture: true,
      passive: true,
    });
  }
  return () => {
    for (const gesture of GESTURES) {
      target.removeEventListener(gesture, onGesture, { capture: true });
    }
  };
}

/** Report the page's gestures while the component using it is mounted — a full session only. */
export function useSessionPresence(): void {
  useEffect(() => listen(document), []);
}

/**
 * Report the gestures made inside a same-origin frame — a document shown as HTML — which the page
 * itself never sees. Given to the iframe's `onLoad`; the listeners go with the frame's document.
 */
export function reportPresenceIn(frame: HTMLIFrameElement): void {
  if (frame.contentDocument) listen(frame.contentDocument);
}
